import { isImageVariable, isTextVariable } from '@carboncopyco/editor-utils';
import type { IVariable } from '@carboncopyco/types';
import { TextMenuHeader } from '@carboncopyco/ui';
import { useEffect, useMemo, useState } from 'react';
import {
  RuleCardDropdown,
  type RuleCardDropdownProps,
  RulesContent,
} from '../../RulesManager';
import { VariableForm } from '../../VariableForm';
import { VariablePreview } from '../../VariablePreview';
import { useCopy } from '../../hooks';

export interface VariableListItemProps {
  variable: IVariable;
  variables: IVariable[];
  onEdit: (variable: IVariable) => Promise<unknown>;
  onDelete: (variable: IVariable) => void;
  open: boolean;
  onOpenRule: () => void;
  onCloseRule: () => void;
  onUseVariable: (variable: IVariable) => void;
}

export const VariableListItem = ({
  open,
  variable,
  variables,
  onOpenRule,
  onCloseRule,
  onEdit,
  onDelete,
  onUseVariable,
}: VariableListItemProps) => {
  const [autoFocus, setAutoFocus] = useState(false);
  const { copyText } = useCopy();

  const dropdownItems = useMemo(
    () =>
      [
        {
          type: 'edit',
          onClick: () => {
            onOpenRule();
            setAutoFocus(true);
          },
        },
        {
          type: 'delete',
          onClick: () => onDelete(variable),
        },
      ] as RuleCardDropdownProps['items'],
    [variable, onDelete, onOpenRule]
  );

  useEffect(() => {
    if (!open) {
      setAutoFocus(false);
    }
  }, [open]);

  return (
    <RulesContent.List.Item
      id={isTextVariable(variable) ? variable.code : variable.variableId}
      header={
        open ? (
          <TextMenuHeader
            label="Variable settings"
            color="text-ds-natural-600"
          />
        ) : (
          <VariablePreview variable={variable} />
        )
      }
      content={
        <VariableForm
          type={variable.type}
          onFocus={() => setAutoFocus(false)}
          autoFocus={autoFocus}
          variable={variable}
          onSubmit={async (updatedVariable) => {
            await onEdit({
              ...variable,
              ...updatedVariable,
            });
            // a small delay before the closing animation
            setTimeout(onCloseRule, 300);
          }}
          variables={variables}
          onCancel={onCloseRule}
        />
      }
      action={
        <RuleCardDropdown
          items={
            isTextVariable(variable)
              ? [
                  {
                    type: 'copy',
                    label: 'Copy placeholder',
                    onClick: () => {
                      copyText(
                        `{{${variable.code}}}`,
                        'Variable placeholder was copied to your clipboard.'
                      );
                    },
                  },
                  ...dropdownItems,
                ]
              : isImageVariable(variable)
                ? [
                    {
                      type: 'variable',
                      onClick: () => onUseVariable(variable),
                    },
                    ...dropdownItems,
                  ]
                : dropdownItems
          }
        />
      }
    />
  );
};
